/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get("announcement-bar")) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector(".announcement__slider");
      this.localization = this.querySelector(".announcement__localization");
      this.links = this.querySelectorAll(".js-announcement-link");
      this.menu = document.querySelector(".main-menu__content");
      this.closeBtn = this.querySelector(".announcement__close");
      this.dots = this.querySelectorAll(".announcement__dot");
      this.isPaused = false;
      this.currentIndex = 0;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener(
          "on:breakpoint-change",
          this.moveLinksHandler,
        );
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener(
          "on:breakpoint-change",
          this.moveLocalizationHandler,
        );
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler =
          this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener("on:breakpoint-change", this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler =
          this.moveLocalizationHandler ||
          debounce(this.moveLocalization.bind(this));
        window.addEventListener(
          "on:breakpoint-change",
          this.moveLocalizationHandler,
        );
      }

      if (this.slider) {
        this.slider.addEventListener("mouseenter", this.pauseSlider.bind(this));
        this.slider.addEventListener(
          "mouseleave",
          this.resumeSlider.bind(this),
        );
      }

      // Dismiss button
      if (this.closeBtn) {
        this.closeBtn.addEventListener("click", () => this.dismiss());
      }

      // Dot navigation
      if (this.dots.length) {
        this.dots.forEach((dot) => {
          dot.addEventListener("click", () => {
            const index = parseInt(dot.dataset.slideIndex, 10);
            this.goToSlide(index);
          });
        });
      }
    }

    /**
     * Initialise the component
     */
    init() {
      // Check if previously dismissed
      if (this.dataset.dismissable === "true") {
        const sectionId = this.dataset.sectionId;
        if (
          sessionStorage.getItem(`announcement-dismissed-${sectionId}`) ===
          "true"
        ) {
          this.classList.add("is-dismissed");
          return;
        }
      }

      if (this.slider) {
        const slides = this.slider.querySelectorAll(".announcement__text");
        slides[0].classList.add("is-visible");
        this.currentIndex = 0;
        this.updateDots(0);

        const nextSlide = () => {
          if (this.canAdvance()) {
            const nextIndex = (this.currentIndex + 1) % slides.length;
            this.transitionSlide(this.currentIndex, nextIndex, slides);
          }

          this.slideTimer = setTimeout(
            nextSlide,
            parseInt(this.dataset.slideDelay, 10),
          );
        };
        this.slideTimer = setTimeout(
          nextSlide,
          parseInt(this.dataset.slideDelay, 10),
        );
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Smoothly transition between two slides with fade + vertical motion
     */
    transitionSlide(fromIndex, toIndex, slides) {
      if (fromIndex === toIndex) return;

      const fromSlide = slides[fromIndex];
      const toSlide = slides[toIndex];

      // Exit current slide
      fromSlide.classList.add("is-exiting");
      fromSlide.classList.remove("is-visible");

      // After exit transition completes, clean up and show next
      setTimeout(() => {
        fromSlide.classList.remove("is-exiting");
        toSlide.classList.add("is-visible");
        this.currentIndex = toIndex;
        this.updateDots(toIndex);
        this.applySlideColors(toSlide);
      }, 250);
    }

    /**
     * Apply the active slide's color overrides to the bar
     */
    applySlideColors(slide) {
      if (!slide || !slide.dataset.bg) return;
      this.style.backgroundColor = slide.dataset.bg;
      this.style.setProperty(
        "--announcement-text-color",
        slide.dataset.textRgb,
      );
    }

    /**
     * Navigate to a specific slide
     */
    goToSlide(index) {
      if (!this.slider || index === this.currentIndex) return;

      const slides = this.slider.querySelectorAll(".announcement__text");
      if (index < 0 || index >= slides.length) return;

      // Reset timer
      clearTimeout(this.slideTimer);
      this.transitionSlide(this.currentIndex, index, slides);

      // Restart auto-advance
      const nextSlide = () => {
        if (this.canAdvance()) {
          const nextIndex = (this.currentIndex + 1) % slides.length;
          this.transitionSlide(this.currentIndex, nextIndex, slides);
        }
        this.slideTimer = setTimeout(
          nextSlide,
          parseInt(this.dataset.slideDelay, 10),
        );
      };
      this.slideTimer = setTimeout(
        nextSlide,
        parseInt(this.dataset.slideDelay, 10),
      );
    }

    /**
     * Update active dot indicator
     */
    updateDots(activeIndex) {
      if (!this.dots.length) return;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle("is-active", i === activeIndex);
        dot.setAttribute("aria-selected", i === activeIndex ? "true" : "false");
      });
    }

    /**
     * Dismiss the announcement bar
     */
    dismiss() {
      this.classList.add("is-dismissed");
      const sectionId = this.dataset.sectionId;
      if (sectionId) {
        sessionStorage.setItem(`announcement-dismissed-${sectionId}`, "true");
      }
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector(
        ".mob__announcement-links",
      );
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement("nav");
        mobNav.classList.add("mob__announcement-links");

        const mobNavUl = document.createElement("ul");
        mobNav.classList.add("secondary-nav");

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector(".announcement__slider");
      this.localization = this.querySelector(".announcement__localization");
      this.links = this.querySelectorAll(".js-announcement-link");
      this.menu = document.querySelector(".main-menu__content");
      const menuLocalization = document.querySelector(".mob__localization");
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement("div");
        mobLocalizationElem.classList.add("mob__localization");
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Whether the slider should advance on this tick.
     *
     * This used to call theme.elementUtil.isInViewport(), which requires
     * rect.top >= 0. The bar sits inside the sticky header, so that test goes
     * false as soon as the page is scrolled at all and the messages silently
     * stop rotating. Partial visibility plus a foreground tab is the check we
     * actually want.
     */
    canAdvance() {
      if (this.isPaused) return false;
      if (document.hidden) return false;
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.bottom > 0 && rect.top < viewportHeight;
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define("announcement-bar", AnnouncementBar);
}
